import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { YourIncomeVariable } from 'enums/LoanFormVariables';

import { PayFrequencyOptionValue } from './yourTotalIncome';

export enum YourIncomeVariant {
  Default,
  CardWaitlist,
  CardCrossSell,
}

export interface YourIncomeResponse {
  employmentIncome: Record<string, string>[];
}

export interface YourIncomeData {
  [YourIncomeVariable.CreditScoreRange]: string | null;
  [YourIncomeVariable.TotalAnnualIncome]: number | null;
  [YourIncomeVariable.StartOfEmployment]: string | null;
  [YourIncomeVariable.EmployerName]?: string;
  [YourIncomeVariable.NotEmployed]: boolean;
  [YourIncomeVariable.JobTitle]?: string;
  [YourIncomeVariable.PayFrequency]?: string;
  [YourIncomeVariable.CredentialAcquisitionDate]?: string;
  [YourIncomeVariable.AdditionalCredential]?: string;
  [YourIncomeVariable.NoOtherCredentials]: boolean;
  [YourIncomeVariable.AdditionalCredentialAcquisitionDate]?: string;
  variant: YourIncomeVariant;
}

export const initialState: YourIncomeData = {
  [YourIncomeVariable.CreditScoreRange]: null,
  [YourIncomeVariable.TotalAnnualIncome]: null,
  [YourIncomeVariable.StartOfEmployment]: null,
  [YourIncomeVariable.NotEmployed]: false,
  [YourIncomeVariable.EmployerName]: '',
  [YourIncomeVariable.JobTitle]: undefined,
  [YourIncomeVariable.PayFrequency]: PayFrequencyOptionValue.BiWeekly,
  [YourIncomeVariable.CredentialAcquisitionDate]: undefined,
  [YourIncomeVariable.AdditionalCredential]: undefined,
  [YourIncomeVariable.NoOtherCredentials]: false,
  [YourIncomeVariable.AdditionalCredentialAcquisitionDate]: undefined,
  variant: YourIncomeVariant.Default,
};

const yourIncomeReducer = createSlice({
  name: 'yourIncome',
  initialState,
  reducers: {
    setYourIncomeData: (state: YourIncomeData, { payload }: PayloadAction<YourIncomeData>) => {
      state[YourIncomeVariable.CreditScoreRange] = payload[YourIncomeVariable.CreditScoreRange];
      state[YourIncomeVariable.TotalAnnualIncome] = payload[YourIncomeVariable.TotalAnnualIncome];
      state[YourIncomeVariable.StartOfEmployment] = payload[YourIncomeVariable.StartOfEmployment];
      state[YourIncomeVariable.EmployerName] = payload[YourIncomeVariable.EmployerName];
      state[YourIncomeVariable.JobTitle] = payload[YourIncomeVariable.JobTitle];
      state[YourIncomeVariable.PayFrequency] = payload[YourIncomeVariable.PayFrequency];
      state[YourIncomeVariable.NotEmployed] = payload[YourIncomeVariable.NotEmployed];
      state[YourIncomeVariable.CredentialAcquisitionDate] = payload[YourIncomeVariable.CredentialAcquisitionDate];
      state[YourIncomeVariable.AdditionalCredential] = payload[YourIncomeVariable.AdditionalCredential];
      state[YourIncomeVariable.NoOtherCredentials] = payload[YourIncomeVariable.NoOtherCredentials];
      state[YourIncomeVariable.AdditionalCredentialAcquisitionDate] =
        payload[YourIncomeVariable.AdditionalCredentialAcquisitionDate];
    },
    setYourIncomeVariant: (state: YourIncomeData, { payload }: PayloadAction<YourIncomeVariant>) => {
      state.variant = payload;
    },
    resetYourIncomeEmployer: (state: YourIncomeData) => {
      state[YourIncomeVariable.EmployerName] = initialState[YourIncomeVariable.EmployerName];
      state[YourIncomeVariable.NotEmployed] = initialState[YourIncomeVariable.NotEmployed];
    },
    resetYourIncomeCredentialDate: (state: YourIncomeData) => {
      state[YourIncomeVariable.CredentialAcquisitionDate] = initialState[YourIncomeVariable.CredentialAcquisitionDate];
      state[YourIncomeVariable.AdditionalCredential] = initialState[YourIncomeVariable.AdditionalCredential];
      state[YourIncomeVariable.NoOtherCredentials] = initialState[YourIncomeVariable.NoOtherCredentials];
      state[YourIncomeVariable.AdditionalCredentialAcquisitionDate] =
        initialState[YourIncomeVariable.AdditionalCredentialAcquisitionDate];
    },
    resetYourIncomeAmount: (state: YourIncomeData) => {
      state[YourIncomeVariable.TotalAnnualIncome] = initialState[YourIncomeVariable.TotalAnnualIncome];
    },
  },
});

export const {
  setYourIncomeData,
  setYourIncomeVariant,
  resetYourIncomeEmployer,
  resetYourIncomeCredentialDate,
  resetYourIncomeAmount,
} = yourIncomeReducer.actions;

export default yourIncomeReducer.reducer;
